import React, { useCallback, useRef, useState } from 'react';

import EditNoteIcon from '@mui/icons-material/EditNote';
import MicIcon from '@mui/icons-material/Mic';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import { Box, Chip, CircularProgress, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';

import { transcribeAudio } from '~/services/AIChatService';

type VoiceState = 'idle' | 'recording' | 'transcribing';

const HAS_MEDIA_DEVICES = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

interface AiChatInputProps {
  onSend: (message: string) => void;
  isLoading: boolean;
  suggestedQuestions: Array<string>;
  showSuggestions: boolean;
  draftActions?: Array<string>;
  showDraftActions?: boolean;
  selectionOptions?: Array<string>;
  selectionLabel?: string | null;
}

export default function AiChatInput({
  onSend,
  isLoading,
  suggestedQuestions,
  showSuggestions,
  draftActions = [],
  showDraftActions = false,
  selectionOptions = [],
  selectionLabel = null,
}: AiChatInputProps) {
  const [input, setInput] = useState('');
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const hasSelectionOptions = selectionOptions.length > 0;

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Array<Blob>>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) {
      return;
    }
    onSend(trimmed);
    setInput('');
  }, [input, isLoading, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleSuggestionClick = useCallback(
    (question: string) => {
      if (isLoading) {
        return;
      }
      onSend(question);
    },
    [isLoading, onSend],
  );

  const handleSelectionClick = useCallback(
    (option: string) => {
      if (isLoading) {
        return;
      }
      onSend(`My department is ${option}.`);
    },
    [isLoading, onSend],
  );

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const handleTranscription = useCallback(async (blob: Blob) => {
    setVoiceState('transcribing');
    setVoiceError(null);
    try {
      const text = await transcribeAudio(blob);
      if (text) {
        setInput((prev) => (prev ? `${prev} ${text}` : text));
      }
    } catch {
      setVoiceError('Transcription failed. Please try again.');
    } finally {
      setVoiceState('idle');
    }
  }, []);

  const startRecording = useCallback(async () => {
    setVoiceError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : undefined;
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        stopStream();
        handleTranscription(blob);
      };

      recorder.start();
      setVoiceState('recording');
    } catch {
      stopStream();
      setVoiceError('Microphone access denied.');
      setVoiceState('idle');
    }
  }, [stopStream, handleTranscription]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const handleMicClick = useCallback(() => {
    if (voiceState === 'recording') {
      stopRecording();
    } else if (voiceState === 'idle') {
      startRecording();
    }
  }, [voiceState, startRecording, stopRecording]);

  const micDisabled = isLoading || voiceState === 'transcribing';

  return (
    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
      {showSuggestions && suggestedQuestions.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
          {suggestedQuestions.map((question) => (
            <Chip
              key={question}
              label={question}
              size="small"
              variant="outlined"
              onClick={() => handleSuggestionClick(question)}
              disabled={isLoading}
              sx={{ maxWidth: '100%' }}
            />
          ))}
        </Stack>
      )}

      {showDraftActions && draftActions.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
          {draftActions.map((action) => (
            <Chip
              key={action}
              icon={<EditNoteIcon />}
              label={action}
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleSuggestionClick(action)}
              disabled={isLoading}
              sx={{ maxWidth: '100%' }}
            />
          ))}
        </Stack>
      )}

      {hasSelectionOptions && (
        <>
          {selectionLabel && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
              {selectionLabel}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
            {selectionOptions.map((option) => (
              <Chip
                key={option}
                label={option}
                size="small"
                color="primary"
                variant="outlined"
                onClick={() => handleSelectionClick(option)}
                disabled={isLoading}
                sx={{ maxWidth: '100%' }}
              />
            ))}
          </Stack>
        </>
      )}

      {voiceError && (
        <Typography variant="caption" color="error" sx={{ mb: 0.5, display: 'block' }}>
          {voiceError}
        </Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="flex-end">
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder={
            hasSelectionOptions
              ? 'Choose a department above...'
              : voiceState === 'recording'
              ? 'Recording...'
              : 'Type a message...'
          }
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading || voiceState === 'recording' || hasSelectionOptions}
        />

        {HAS_MEDIA_DEVICES && (
          <Tooltip title={voiceState === 'recording' ? 'Stop recording' : 'Voice input'}>
            <span>
              <IconButton
                onClick={handleMicClick}
                disabled={micDisabled}
                color={voiceState === 'recording' ? 'error' : 'default'}
              >
                {voiceState === 'transcribing' ? (
                  <CircularProgress size={24} />
                ) : voiceState === 'recording' ? (
                  <StopIcon />
                ) : (
                  <MicIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}

        <IconButton color="primary" onClick={handleSend} disabled={!input.trim() || isLoading || hasSelectionOptions}>
          <SendIcon />
        </IconButton>
      </Stack>
    </Box>
  );
}
